import api from '@/services/api';
import type { AgentType, Substage } from '@/modules/job/store/useJobWizard';
import type { CvScreeningSnapshot } from '@/modules/application/services/application.api';

const BASE = '/v2/job';

export interface JobSalary {
    min?: number;
    max?: number;
    currency?: string;
    period?: 'hourly' | 'monthly' | 'yearly';
    isVisible?: boolean;
}

export interface HiringTeamMember {
    userId: string;
    role: 'recruiter' | 'hiring_manager' | 'interviewer' | 'coordinator';
    addedAt?: string;
}

export interface JobSettings {
    isRemote?: boolean;
    requireCV?: boolean;
    requireCoverLetter?: boolean;
    applicationDeadline?: string | null;
    autoRejectAfterDays?: number | null;
}

export interface PipelineStage {
    _id: string;
    name: string;
    order: number;
    stageKind: 'applied' | 'default' | 'agentic';
    agentType?: AgentType | null;
    agentConfigId?: string | null;
    agentConfigStatus?: 'unconfigured' | 'draft' | 'active' | null;
    substages?: Substage[];
    isSystem?: boolean;
}

export interface Pipeline {
    _id: string;
    organizationId: string;
    workspaceId: string;
    jobPostingId: string;
    name: string;
    stages: PipelineStage[];
    createdAt: string;
    updatedAt: string;
}

export interface DocumentConfig {
    type: string;
    label: string;
    required: boolean;
}

export interface JobFieldDefinition {
    key: string;
    label: string;
    type: string;
    required: boolean;
    options: string[];
    description?: string;
}

export interface ApplicationFormField {
    key: string;
    label: string;
    type: string;
    required: boolean;
    options: string[];
    isDefault: boolean;
    sourceScope?: string;
}

export interface HiringSetupPayload {
    postToJobBoard: boolean;
    sources: ('auto-apply' | 'application-form' | 'sourcing' | 'ats-import')[];
    agents: ('cv-screening' | 'ai-interview')[];
}

export interface JobPosting {
    _id: string;
    organizationId: string;
    workspaceId: string;
    title: string;
    description: string;
    requirements: string;
    department?: string;
    location?: string;
    employmentType: string;
    salary: JobSalary;
    status: 'draft' | 'open' | 'paused' | 'closed' | 'archived';
    pipelineId?: string;
    hiringTeam: HiringTeamMember[];
    settings: JobSettings;
    applicationForm?: {
        documents: DocumentConfig[];
        jobFields: JobFieldDefinition[];
        fields: ApplicationFormField[];
    };
    hiringSetup?: HiringSetupPayload | null;
    icp?: {
        mustHaveSkills?: string[];
        niceToHaveSkills?: string[];
        experienceRange?: { min: number; max: number };
        seniorityLevel?: string;
        industryBackground?: string[];
        educationPreferences?: string[];
        culturalFitSignals?: string[];
        dealBreakers?: string[];
        summary?: string;
        markdown?: string;
        candidates?: any[];
        status?: 'in_progress' | 'complete';
        generatedAt?: string;
    } | null;
    magicLinkKey?: string;
    createdBy: string;
    closedAt?: string;
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
    pipeline?: Pipeline;
}

export interface JobListResult {
    results: JobPosting[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface OverviewUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    name: string;
    initials: string;
    avatarUrl: string;
}

export interface OverviewSource {
    key: string;
    kind: 'job_board' | 'referral' | 'api' | 'application_form' | 'sourcing' | 'manual' | string;
    source: string;
    label: string;
    count: number;
}

export interface OverviewApplication {
    _id: string;
    candidate: {
        name: string;
        email?: string;
        phone?: string;
        avatarUrl?: string | null;
    };
    source: string;
    sourceLabel: string;
    sourceKind: string;
    currentStageId: string | null;
    currentStageName: string;
    currentSubstage: string | null;
    appliedAt: string;
    updatedAt: string;
    verdict: string | null;
    activeCvScreening: CvScreeningSnapshot | null;
    tileState: 'normal' | 'action' | 'failed' | 'parked' | 'loading' | 'invited' | 'in_review' | string;
    /** Reviewer roll-up — populated when the application is in a human-review
     *  flow. UI uses these to render the corner badge (count or green tick). */
    reviewersAssigned?: number;
    reviewersCompleted?: number;
    reviewersPending?: number;
    allReviewersDone?: boolean;
}

export interface OverviewAutomationRule {
    _id: string;
    name: string;
    description: string;
    enabled: boolean;
    executeOnce: boolean;
    order: number;
    trigger: { type: string; config?: Record<string, unknown> } | null;
    conditions: Array<{ field: string; operator: string; value: unknown }>;
    actions: Array<{
        type: string;
        targetStageId: string | null;
        status: string | null;
        reason: string | null;
        tag: string | null;
        delayMinutes: number;
    }>;
    stats: {
        executionCount?: number;
        lastExecutedAt?: string | null;
        lastError?: string | null;
    };
}

export interface OverviewPipelineStage extends PipelineStage {
    count: number;
    applications: OverviewApplication[];
    automation: {
        rules: OverviewAutomationRule[];
        enabledRules: number;
        eventStats: {
            total: number;
            executed: number;
            failed: number;
            lastEventAt: string | null;
            avgLatencyMs: number;
        };
        logStats: {
            total: number;
            failed: number;
            lastLogAt: string | null;
        };
    };
}

export interface OverviewJob {
    _id: string;
    title: string;
    department: string;
    location: string;
    employmentType: string;
    status: JobPosting['status'];
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
    createdBy: string | null;
    createdByUser: OverviewUser | null;
    hiringTeam: Array<{
        userId: string;
        role: HiringTeamMember['role'];
        addedAt?: string;
        user: OverviewUser | null;
    }>;
    hiringSetup: HiringSetupPayload | null;
    settings: JobSettings;
    sources: OverviewSource[];
    candidateCount: number;
    /** Number of applications in the role with at least one pending reviewer
     *  (assigned > completed). Drives the "X to review" eyebrow tag. */
    pendingReviewCount?: number;
    pipeline: (Omit<Pipeline, 'stages'> & { stages: OverviewPipelineStage[] }) | null;
    automationSummary: {
        rules: number;
        enabledRules: number;
        failed: number;
        lastActivityAt: string | null;
    };
}

export interface JobsOverviewResult {
    totals: {
        jobs: number;
        openJobs: number;
        candidates: number;
        activeAutomations: number;
        failedAutomations: number;
    };
    recentJobs: Array<{
        _id: string;
        title: string;
        status: JobPosting['status'];
        candidateCount: number;
    }>;
    jobs: OverviewJob[];
}

/** Per-job sourcing + outreach summary for the /jobs roster page — one batch
 *  call replaces the old two-full-state-calls-per-card fan-out. Each lane is
 *  null when the job never set it up (the card hides that lane). Sourcing
 *  mirrors the searches-v3 list read model (state, schedule, 7-day trend,
 *  pending review) so both roster surfaces speak the same language. */
export interface JobSourcingSummary {
    status: 'off' | 'active' | 'paused' | 'exhausted';
    poolTotal: number;
    deliveredCount: number;
    pendingReviewCount: number;
    newToday: number;
    dailyTarget: number | null;
    scheduleHour: number | null;
    timezone: string | null;
    /** Deliveries per local day, oldest → today (7 buckets). */
    sourced7d: number[];
    lastRunStatus: 'queued' | 'running' | 'completed' | 'exhausted' | 'failed' | null;
}

export interface JobOutreachSummary {
    status: 'draft' | 'ready' | 'active' | 'paused' | 'completed' | 'archived';
    total: number;
    byStage: Record<string, number>;
}

export interface JobRosterSummary {
    sourcing: JobSourcingSummary | null;
    outreach: JobOutreachSummary | null;
}

export const jobApi = {
    listJobs: (params?: {
        status?: string;
        notStatus?: string;
        search?: string;
        page?: number;
        limit?: number;
        sort?: string;
    }) => api.get<JobListResult>(BASE, { params }),

    getJobsOverview: (params?: { search?: string; status?: string; mineOnly?: boolean; limit?: number }) =>
        api.get<JobsOverviewResult>(`${BASE}/overview`, { params }),

    getRosterSummary: (ids: string[]) =>
        api.get<{ summaries: Record<string, JobRosterSummary> }>(`${BASE}/roster-summary`, {
            params: { ids: ids.join(',') },
        }),

    getJob: (id: string) => api.get<JobPosting>(`${BASE}/${id}`),

    createJob: (data: {
        title: string;
        description?: string;
        requirements?: string;
        department?: string;
        location?: string;
        employmentType?: string;
        salary?: JobSalary;
        settings?: JobSettings;
        hiringTeam?: HiringTeamMember[];
        stages?: {
            name: string;
            stageKind: string;
            order?: number;
            agentType?: string;
        }[];
    }) => api.post<JobPosting>(BASE, data),

    updateJob: (id: string, data: Record<string, any>) => api.patch<JobPosting>(`${BASE}/${id}`, data),

    archiveJob: (id: string) => api.delete<JobPosting>(`${BASE}/${id}`),

    publishJob: (id: string) => api.post<JobPosting>(`${BASE}/${id}/publish`),

    updateHiringTeam: (id: string, hiringTeam: HiringTeamMember[]) =>
        api.patch<HiringTeamMember[]>(`${BASE}/${id}/hiring-team`, { hiringTeam }),

    generateJob: (prompt: string, hiringSetup?: HiringSetupPayload) =>
        api.post<JobPosting>(`${BASE}/generate`, { prompt, hiringSetup }),

    // Lightweight draft — no Grok, just a JobPosting + Pipeline so the
    // AI Profiling flow has a real ObjectId URL before the ICP agent starts.
    createDraftJob: (description: string) => api.post<JobPosting>(`${BASE}/draft`, { description }),

    sendHiringManagerInvite: (id: string, email: string) =>
        api.post<{ ok: true }>(`${BASE}/${id}/send-hm-invite`, { email }),

    /**
     * Rotate the magic-link key for the HM public ICP flow. Invalidates
     * any previously-shared link for this job. Returns the fresh key
     * so the caller can re-render the share URL inline.
     */
    rotateMagicLink: (id: string) => api.post<{ magicLinkKey: string }>(`${BASE}/${id}/rotate-magic-link`),

    generateEvaluation: (id: string) => api.post(`${BASE}/${id}/generate-evaluation`),
};
